import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';

import {
  Exact,
  MeProviderProgramTagsQuery,
  PatientLifecycleStatusV2,
  PatientTableDataFragment,
  ProgramTagDataFragment,
} from '../../../../../generated/graphql';

import { getRelativeTimestamp, getShortDate } from '../../../../lib/time';
import { AnalyticsPage } from '../../../../../lib/analytics';
import Avatar from '../../../../components/Avatar';
import NoText from '../../../../components/NoText';
import TestClientPill from '../../../../components/TestClientPill';
import { PatientCallback } from '../../helpers';
import {
  ChatIcon,
  MailIcon,
  PencilAltIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import ProgramTagManager from '../../../../components/ProgramTag/ProgramTagManager';
import { ApolloQueryResult } from '@apollo/client';
import Bolt from '../../../../svgs/Bolt';
import { isPatientNewFromStorefront } from '../../../../lib/patient';
import StackedAvatars from '../../../ClientSessions/components/StackedAvatars';
import { useNavigate } from 'react-router-dom';
import AssignProviderUsersModal from '../../../../components/Modals/ClientAssignmentModals/AssignProviderUsersModal';
import { usePatientLazyQuery } from '../../../../../generated/graphql';
import { useColumnVisibility } from './useColumnVisibility';

type ActiveRowProps = {
  isChecked: boolean;
  patient: PatientTableDataFragment;
  patientIndex: number;
  patientLifecycleStatus?: PatientLifecycleStatusV2;
  nextStepsButtonsDisabled: boolean;
  navigateToPatientFollowUp: PatientCallback;
  navigateToPatientMessages: PatientCallback;
  navigateToPatientProfile: (
    patient: PatientTableDataFragment,
    subRoute?: string,
  ) => void;
  setIsSendAssessmentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSinglePatient: React.Dispatch<
    React.SetStateAction<PatientTableDataFragment | null>
  >;
  providerProgramTags?: ProgramTagDataFragment[];
  refetchProviderProgramTags?: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<MeProviderProgramTagsQuery>>;
  handleProgramTagsUpdate: (
    programId: string,
    tags: ProgramTagDataFragment[],
  ) => void;
  visibleColumns: string[];
  visibleActions?: string[];
  handleRowCheckboxChange?: (
    patient: PatientTableDataFragment,
    patientIndex: number,
    isChecked: boolean,
    isShiftKeyPressed: boolean,
  ) => void;
  setLastSelectedPatientIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const ActiveRow: FC<ActiveRowProps> = ({
  isChecked,
  patient,
  patientIndex,
  navigateToPatientProfile,
  navigateToPatientFollowUp,
  navigateToPatientMessages,
  providerProgramTags = [],
  refetchProviderProgramTags,
  handleProgramTagsUpdate,
  visibleColumns,
  visibleActions = [],
  handleRowCheckboxChange,
  setLastSelectedPatientIndex,
}) => {
  const hasUnreadComments = patient?.aggregateUnreadCommentCount > 0;
  const isNewStorefrontPatient = isPatientNewFromStorefront(patient);
  const navigate = useNavigate();

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [fetchPatient, { data: patientDetails }] = usePatientLazyQuery();
  const selectedPatient = useMemo(
    () => patientDetails?.patient ?? null,
    [patientDetails],
  );
  const [localAssigned, setLocalAssigned] = useState(
    patient.assignedProviderUsers ?? [],
  );

  const { shouldShowColumn } = useColumnVisibility(visibleColumns);

  return (
    <tr
      className={classNames(
        'group max-w-full cursor-pointer whitespace-nowrap text-green-150',
        isChecked && 'bg-neutral-25',
        !isChecked && 'hover:bg-secondary-20',
      )}
      onClick={(event) => {
        navigateToPatientProfile?.(patient);
        event.stopPropagation();
      }}
    >
      <td className="w-[3.75rem]">
        <div className="flex justify-center">
          {isNewStorefrontPatient && (
            <Bolt className="h-[1.125rem] w-[1.125rem] text-orange-100" />
          )}
        </div>
      </td>

      <td className="w-[3.75rem]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center">
          <input
            type="checkbox"
            aria-label={`Select ${patient.firstName} ${patient.lastName}`}
            checked={isChecked}
            onClick={(e) => {
              e.stopPropagation();
              handleRowCheckboxChange?.(
                patient,
                patientIndex,
                !isChecked,
                e.shiftKey,
              );
            }}
            onChange={(e) => e.stopPropagation()}
            className="h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-2 focus:ring-secondary-125"
          />
        </div>
      </td>

      {shouldShowColumn('name') && (
        <td className="py-4 px-0">
          <div className="flex flex-row items-center gap-x-4">
            <Avatar
              imageUrl={patient?.profileImageMedia?.url}
              name={patient?.firstName ?? ''}
              size="medium"
            />
            <div className="font-serif text-extra-small">
              {patient?.firstName + ' ' + patient?.lastName}
            </div>
            {patient?.isTestProgram && <TestClientPill />}
          </div>
        </td>
      )}

      {shouldShowColumn('careTeam') && (
        <td className="py-4 px-0">
          <div
            role="button"
            tabIndex={0}
            className="-my-4 block w-full py-4 px-2 text-left focus:outline-none"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await fetchPatient({
                variables: { programId: patient.programInstanceId },
              });
              setAssignModalOpen(true);
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                await fetchPatient({
                  variables: { programId: patient.programInstanceId },
                });
                setAssignModalOpen(true);
              }
            }}
            title="Manage assignment"
          >
            <div className="pointer-events-none">
              {localAssigned?.length ? (
                <StackedAvatars
                  items={localAssigned.map((user) => ({
                    name: user.name || user.email,
                    description: user.email,
                    imageUrl: user.profileImageMedia?.url,
                  }))}
                />
              ) : (
                <div className="inline-flex items-center rounded-3xl bg-neutral-50 px-3 py-1.5 font-sans text-small-caption font-medium text-neutral-125 hover:bg-neutral-75">
                  <PlusIcon className="mr-2 h-4 w-4" />
                  <span>Assign</span>
                </div>
              )}
            </div>
          </div>
          <AssignProviderUsersModal
            isOpen={assignModalOpen}
            setClosed={() => setAssignModalOpen(false)}
            selectedPatient={selectedPatient}
            refetchPatient={async (vars) => {
              const res = await fetchPatient({
                variables: {
                  programId: patient.programInstanceId,
                  ...vars,
                },
              });
              const updated = res?.data?.patient?.assignedProviderUsers ?? [];
              setLocalAssigned(updated);
              return res;
            }}
          />
        </td>
      )}

      {shouldShowColumn('tags') && (
        <td className="max-w-[300px]">
          <ProgramTagManager
            key={JSON.stringify(patient.tags)}
            programId={patient.programInstanceId}
            availableProgramTags={providerProgramTags}
            refreshAvailableProgramTags={refetchProviderProgramTags}
            selectedProgramTags={patient.tags}
            onTagsUpdated={(tags) => {
              handleProgramTagsUpdate(patient.programInstanceId, tags);
            }}
            analyticsPage={AnalyticsPage.ClientsTableActive}
            className="w-full max-w-full"
          />
        </td>
      )}

      {shouldShowColumn('programStartDate') && (
        <td className="py-4 px-0">
          {patient.programStartDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programStartDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('programEndDate') && (
        <td className="py-4 px-0">
          {patient.programEndDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programEndDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('dateAdded') && (
        <td className="py-4 px-0">
          {patient.createdAt ? (
            <span className="text-small-caption">
              {getShortDate(patient.createdAt)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('unreadComments') && (
        <td>
          {hasUnreadComments ? (
            <div className="flex flex-row items-center gap-x-3 text-caption">
              <div className="flex h-7 w-7 flex-row items-center justify-center rounded-full bg-secondary-100 text-small-caption font-medium text-neutral-0">
                {patient.aggregateUnreadCommentCount}
              </div>
              <span className="text-neutral-110">
                {getRelativeTimestamp(patient.latestUnreadCommentAt)}
              </span>
            </div>
          ) : (
            <NoText text="None" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('sendFollowUp') && visibleActions.length > 0 && (
        <td>
          <div className="flex flex-row">
            {visibleActions.includes('actionChat') && (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigateToPatientMessages(patient);
                }}
                className="mr-2 rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
              >
                <ChatIcon className="h-5 w-5 text-neutral-125" />
              </button>
            )}
            {visibleActions.includes('actionFollowUp') && (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigateToPatientFollowUp(patient);
                }}
                className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
              >
                <MailIcon className="h-5 w-5 text-neutral-125" />
              </button>
            )}
            {visibleActions.includes('actionNotes') && (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigate(`/clients/${patient.programInstanceId}/notes`);
                }}
                className="ml-2 rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
                title="Private notes"
              >
                <PencilAltIcon className="h-5 w-5 text-neutral-125" />
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default ActiveRow;
